import { Trans as TransComponent } from 'react-i18next/TransWithoutContext';

/**
 * ===========================================================================
 * 🔴 `values` TRÄGT GRUNDWERTE — UND DAS IST DER GANZE ZWECK DIESES UMSCHLAGS
 * ===========================================================================
 *
 * Der Anlass (12.09.2026, M-6, vom Gerät gemeldet). Auf der Termine-Fläche
 * stand wörtlich:
 *
 *     „Locari lädt ein. [object Object] freie Plätze, 0 warten."
 *
 * `termine-nebenspalte.tsx` gab `values={{ frei, wartende }}`, wobei `frei`
 * ein `FreiePlaetze { plaetze, bis }` ist — der Satz will die **Zahl**. Zwölf
 * Zeilen darüber stand derselbe Wert richtig ausgepackt (`freie: frei.plaetze`).
 *
 * 🔴 **`turbo typecheck` war grün**, und zwar zu Recht: react-i18next
 * deklariert `values` als `{ [key: string]: unknown }`. Jeder Wert passt
 * dorthin, auch einer, den `i18next` am Ende nur noch `String()`-en kann.
 *
 * ⇒ Hier wird die Eigenschaft **verengt**. Was interpoliert werden darf, sind
 * Grundwerte und `Date` (i18next formatiert es selbst). Ein Objekt, ein Feld
 * oder ein React-Element scheitert ab jetzt am Typprüfer statt auf dem
 * Bildschirm eines Kunden.
 *
 * ⚠️ **Bauteile gehören nach `components`, nicht nach `values`** — dafür ist
 * die Eigenschaft da, und sie bleibt unangetastet.
 *
 * ⚠️ **Was diese Verengung NICHT sieht:** einen Platzhalter, den der Satz gar
 * nicht führt (`values={{ tippfehler: 3 }}` bleibt still), und einen
 * fehlenden Wert zu einem vorhandenen Platzhalter. Beides sind Fragen an den
 * Katalog, nicht an den Aufruf — sie liegen bei `check:i18n-raw-keys` und
 * `check:i18n-parity`.
 */
type Interpolierbar = string | number | boolean | Date | null | undefined;

/**
 * ===========================================================================
 * 🔴 N-98 · `count` IST ENGER ALS DER REST — `null` BRICHT DEN PLURAL STILL
 * ===========================================================================
 *
 * `null` ist für einen gewöhnlichen Platzhalter in Ordnung: er rendert leer,
 * und das kann gewollt sein. Für `count` ist es das nicht — i18next wählt
 * daran die Plural-Form.
 *
 * GEMESSEN am 19.09.2026 gegen das i18next AUS DEM BAUM (26.0.2), ein
 * Plural-Satz mit `{{count}}`, vier Werte gefahren:
 *
 *     count: 3          → „Prüfung abgebrochen. 3 Bewerber waren …"
 *     count: 0          → „Prüfung abgebrochen. 0 Bewerber waren …"
 *     count: null       → „Prüfung abgebrochen.  Bewerber waren …"   🔴
 *     count: undefined  → „abgebrochen"                              (roher Schlüssel)
 *
 * Bei `null` verschwindet der Platzhalter **spurlos** — es bleibt ein
 * doppeltes Leerzeichen, sonst nichts. `undefined` ist dagegen sichtbar, weil
 * der rohe Schlüssel stehenbleibt. Die stille Variante ist die gefährliche.
 *
 * 🔴 **Der Typprüfer sah bei `count` bis hierher ÜBERHAUPT NICHTS** — nicht
 * nur `null`, auch `count: 'drei'` war grün. Gegenprobe gefahren, Auflösung
 * gegen die echte `.d.ts` verifiziert. Ursache: i18next deklariert zwar
 * `count?: number` (`typescript/options.d.ts:741`), verschneidet die Optionen
 * aber mit `$Dictionary<unknown>` = `{ [key: string]: unknown }`
 * (`options.d.ts:806`, `helpers.d.ts:3`) — die Index-Signatur hebt die
 * Deklaration auf.
 *
 * ⚠️ **i18nexts eigene Typaugmentation hilft hier nicht:** `CustomTypeOptions`
 * steuert `resources`, `defaultNS`, `returnNull`, `strictKeyChecks` — einen
 * Schalter für `count` gibt es nicht. Und ein Textriegel (`check:*`) kann
 * Nullbarkeit nicht sehen; sie ist eine Eigenschaft des Typs, nicht des
 * Zeichenbestands. Deshalb steht die Verengung hier, wo der Umschlag ohnehin
 * schon einen Typ setzt.
 *
 * ⚠️ **WAS DIESE VERENGUNG NICHT SIEHT: die `t(…, { count })`-Aufrufe.** Sie
 * gehen nicht durch diesen Umschlag — gemessen sind es **44** gegen **179**
 * hier gedeckte. Für sie gilt weiter nur die Konvention. Der Grund, dass der
 * Umschlag trotzdem trägt, ist `no-restricted-imports` in
 * `apps/web/eslint.config.mjs`: `Trans` darf **nur** von hier kommen, an
 * diesem Typ also kein Weg vorbei.
 */
type TransWerte = Record<string, Interpolierbar> & { count?: number };

type TransEigenschaften = Omit<
  React.ComponentProps<typeof TransComponent>,
  'values'
> & {
  values?: TransWerte;
};

export function Trans(props: TransEigenschaften) {
  return <TransComponent {...props} />;
}
